import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { appVersionAtom } from 'state/app-version'
import { APP_VERSION } from 'app-constants'
import useInterval from './use-interval'

const ONE_HOUR_IN_MILLISECONDS = 60 * 60 * 1000

function useFetchAppVersion() {
  const [appVersion, setAppVersion] = useRecoilState(appVersionAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAppVersionStale, setIsAppVersionStale] = useState<boolean>(false)

  // every hour, we should perform a fetch for the latest app version
  useInterval(() => {
    refetchAppVersion()
  }, ONE_HOUR_IN_MILLISECONDS)

  const refetchAppVersion = useCallback(async () => {
    // if we're in development environment, we should not be fetching the app version
    if (import.meta.env.MODE !== 'production') {
      return
    }
    setIsLoading(true)

    try {
      axios.get(APP_VERSION).then((response) => {
        // on first load, appVersion will be empty string, so we should set it
        if (appVersion === '') {
          setAppVersion(response.data.version)
        } else if (appVersion !== response.data.version) {
          // if the app version has changed, on any subsequent fetches, we should show the user a banner to refresh
          setIsAppVersionStale(true)
        }
      })
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [setAppVersion, appVersion])

  useEffect(() => {
    refetchAppVersion()
  }, [refetchAppVersion])

  return {
    appVersion,
    setAppVersion,
    isAppVersionStale,
    isLoading,
    error,
  }
}

export { useFetchAppVersion }
